import CircularProgress from "material-ui-old/CircularProgress";
import _get from "lodash/get";
import _isNil from "lodash/isNil";
import { denormalize } from "normalizr";
import * as qs from "query-string";
import * as React from "react";
import ReactHighcharts from "react-highcharts";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { icons as DRIVER_ICONS } from "../../../assets/svg/drivers";
import { shouldLoadOnMount } from "../../../modules/asyncHttpAction";
import { pageResultsSchema, pageSchema } from "../../../schemas";
import * as styles from "../../../styles/components/Drivers.css";
import Datatable from "../../Datatable";
import Modal from "../../Modal";
import PageDriversGraph from "../PageDriversGraph";
import ScrollLockDiv from "../../ScrollLockDiv";
import DRIVER_INFO, { DRIVER_ORDER } from "./info";

const GENDER_AUDIENCE_ID = 2;
const AGE_AUDIENCE_ID = 3;

function findSegmentByAudienceId(point, audienceId) {
    return (
        point.segmentIds &&
        point.segmentIds.find(segment => {
            // segment can be null if we don't have the segment data in the Redux store. See
            // https://trello.com/c/Xveek031
            return segment && segment.audienceId === audienceId;
        })
    );
}

function makeCellRendererForAudienceSegment(audienceId) {
    return point => {
        const segment = findSegmentByAudienceId(point, audienceId);
        if (!segment) {
            return "-";
        }
        return segment.label;
    };
}

const DATATABLE_COLUMNS = [
    Datatable.createColumn({
        renderHeaderCell: () => <h5>Quote</h5>,
        renderBodyCell: point => (
            <div className={styles.quoteCell}>{point.quote}</div>
        ),
        cellWidthPercentage: 70
    }),
    Datatable.createColumn({
        renderHeaderCell: () => <h5>Gender</h5>,
        renderBodyCell: makeCellRendererForAudienceSegment(GENDER_AUDIENCE_ID),
        cellWidthPercentage: 15
    }),
    Datatable.createColumn({
        renderHeaderCell: () => <h5>Age</h5>,
        renderBodyCell: makeCellRendererForAudienceSegment(AGE_AUDIENCE_ID),
        cellWidthPercentage: 15
    })
];

class Drivers extends React.PureComponent {
    constructor(props) {
        super(props);

        this.handleDriverSelection = this.handleDriverSelection.bind(this);
        this.handleShowModal = this.handleShowModal.bind(this);
        this.handleHideModal = this.handleHideModal.bind(this);

        this.state = {
            modalShowing: false
        };
    }

    componentWillMount() {
        if (
            !this.props.drivers ||
            shouldLoadOnMount(this.props.pageResultRequest)
        ) {
            this.props.fetchPageResults(this.props.pageId);
        }

        // This code shamelessly borrowed from
        // https://stackoverflow.com/questions/30669241/how-can-i-limit-the-length-of-a-highcharts-plotline-on-the-y-axis-for-a-boxplot/30670879#30670879
        ReactHighcharts.Highcharts.Renderer.prototype.symbols.hline = (
            x,
            y,
            width,
            height
        ) => {
            // highcharts gives us a rect with its top-left corner at (x, y) and dimensions width x height
            // we want our line to be centered vertically and take up the entire horizontal space
            return ["M", x, y + height / 2, "L", x + 30, y + height / 2];
        };
    }

    componentDidUpdate(prevProps) {
        if (!this.props.drivers && prevProps.pageId !== this.props.pageId) {
            this.props.fetchPageResults(this.props.pageId);
        }
    }

    handleDriverSelection(event, index) {
        if (event) {
            event.preventDefault();
        }
        this.setState({ driverIndex: index });
    }

    setModalState(event, state) {
        if (event) {
            event.preventDefault();
        }
        this.setState({ modalShowing: state });
    }

    handleShowModal(event) {
        this.setModalState(event, true);
    }

    handleHideModal(event) {
        this.setModalState(event, false);
    }

    static renderErrorMessage(message) {
        return (
            <div className={styles.chapter}>
                <div className={styles.chapterHeader}>
                    <h2 className={styles.chapterTitle}>Drivers</h2>
                    <p className={styles.chapterSubtitle}>
                        How this page scored on the five WEVO conversion
                        drivers.
                    </p>
                </div>
                <p className={styles.noDataMessage}>{message}</p>
            </div>
        );
    }

    render() {
        if (!this.props.drivers) {
            return (
                <div className={styles.chapter}>
                    <div className={styles.chapterHeader}>
                        <h2 className={styles.chapterTitle}>Drivers</h2>
                        <p className={styles.chapterSubtitle}>
                            How this page scored on the five WEVO conversion
                            drivers.
                        </p>
                    </div>
                    <div>
                        <CircularProgress />
                    </div>
                </div>
            );
        }

        if (this.props.drivers.length === 0) {
            return Drivers.renderErrorMessage("No data for drivers.");
        }

        const indexedDrivers = this.props.drivers.reduce((acc, driver) => {
            acc[driver.driver] = driver;
            return acc;
        }, {});
        const drivers = DRIVER_ORDER.map(driver => indexedDrivers[driver]);
        if (drivers.some(_isNil)) {
            return Drivers.renderErrorMessage("Missing driver data.");
        }

        const pageImage = _get(this.props.page, ["image", "files", "original"]);

        const selectedDriver = indexedDrivers[this.props.selectedDriver];
        const driverIcon = DRIVER_ICONS[selectedDriver.driver];
        const driverInfo = DRIVER_INFO[selectedDriver.driver];
        const quotes = selectedDriver.quotes.sort(
            (point1, point2) => point2.quoteScore - point1.quoteScore
        );
        return (
            <div className={styles.chapter}>
                {this.state.modalShowing && (
                    <Modal showing={true} onDismiss={this.handleHideModal}>
                        <div className={styles.modalContent}>
                            <h2>{selectedDriver.driver} Recommendations</h2>
                            <div
                                className={styles.modalScrollableContent}
                                data-scrollable={true}
                            >
                                {driverInfo.recommendations.map(rec => (
                                    <div
                                        key={`${selectedDriver.driver}-${
                                            rec.title
                                        }`}
                                        className={
                                            styles.driverModalRecommendation
                                        }
                                    >
                                        <p className={styles.driverModalTitle}>
                                            {rec.title}
                                        </p>
                                        <div className={styles.driverModalText}>
                                            {rec.text}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Modal>
                )}

                <div className={styles.chapterHeader}>
                    <h2 className={styles.chapterTitle}>Drivers</h2>
                    <p className={styles.chapterSubtitle}>
                        How this page scored on the five WEVO conversion
                        drivers.
                    </p>
                </div>

                <div className={styles.resultsContentRow}>
                    {/* Page image */}
                    <div
                        className={`${styles.resultsContentRowCell} ${
                            styles.driverPage
                        }`}
                    >
                        <h3 className={styles.driverSectionHeading}>
                            {this.props.page.name}
                        </h3>
                        <div className={styles.driverPageImage}>
                            <ScrollLockDiv
                                className={styles.pageImageContainer}
                            >
                                <img
                                    className={styles.pageImage}
                                    src={pageImage.imageUrl}
                                />
                            </ScrollLockDiv>
                        </div>
                    </div>

                    {/* Driver scores graph */}
                    <div
                        className={`${styles.resultsContentRowCell} ${
                            styles.driverScores
                        }`}
                    >
                        <h3 className={styles.driverSectionHeading}>
                            Driver Scores
                        </h3>
                        <div className={styles.driverChart}>
                            <PageDriversGraph pageId={this.props.pageId} />
                        </div>
                        <div className={styles.driverLabel}>
                            <div className={styles.benchmarkDash} />
                            <p>Industry benchmark</p>
                        </div>
                    </div>
                </div>

                <div className={styles.resultsContentRow}>
                    <div
                        className={`${styles.resultsContentRowCell} ${
                            styles.driverRecommendations
                        }`}
                    >
                        <h3 className={styles.driverSectionHeading}>
                            Quotes from Your Target Audience
                        </h3>

                        {/* Driver selection bar */}
                        <div className={styles.driverSelection}>
                            {drivers.map(driver => (
                                <Link
                                    key={driver.driver}
                                    className={`${
                                        styles.driverSelectionLink
                                    } ${driver === selectedDriver &&
                                        styles.current}`}
                                    to={{
                                        search: qs.stringify({
                                            driver: driver.driver,
                                            selected: this.props.page.id
                                        })
                                    }}
                                >
                                    {driver.driver}
                                </Link>
                            ))}
                        </div>

                        {/* Selected driver header & score */}
                        <div className={styles.driverInfo}>
                            <img
                                src={driverIcon}
                                className={styles.driverIcon}
                            />
                            <p className={styles.driverScore}>
                                {selectedDriver.driver} Score:{" "}
                                {Math.round(selectedDriver.driverScore)}
                                /35
                            </p>
                            <p className={styles.driverText}>
                                {driverInfo.definition}
                            </p>
                        </div>

                        {/* Quotes for selected driver */}
                        <Datatable
                            className={styles.driverQuotesTable}
                            columns={DATATABLE_COLUMNS}
                            data={quotes || []}
                            style={{ paddingTop: 0 }}
                        />

                        {/* Link to open recommendations modal */}
                        <p className={styles.driverRecommendations}>
                            {selectedDriver.driver} Low?{" "}
                            <a
                                className={styles.driverRecommendationsLink}
                                onClick={this.handleShowModal}
                            >
                                View Recommendations
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state, props) {
    const { pageId } = props;
    const { pageResultRequest } = state.results;
    const page = denormalize(pageId, pageSchema, state.api) || null;
    const pageResults =
        denormalize(pageId, pageResultsSchema, state.api) || null;

    const { queryString } = state;
    let selectedDriver = DRIVER_ORDER[0];
    if (
        queryString &&
        queryString.driver &&
        DRIVER_ORDER.includes(queryString.driver)
    ) {
        selectedDriver = queryString.driver;
    }

    return {
        page,
        pageResultRequest,
        selectedDriver,
        drivers: pageResults && pageResults.drivers
    };
}

export default connect(
    mapStateToProps,
    null
)(Drivers);



// WEBPACK FOOTER //
// ./src/components/Results/Drivers/index.js